/* Mock routes — plants (the user's garden). */

import type { Plant } from '@core/types'
import { ApiError } from '../../http'
import { db, nextId } from '../store'
import { readString, type MockRoute } from '../types'
import { addDays, awardPoints, healthRank, requirePlant } from './shared'

export const plantRoutes: MockRoute[] = [
  {
    method: 'GET',
    pattern: '/plants',
    handler: ({ query }) => {
      const health = query.get('health')
      const governorate = query.get('governorate')
      const search = query.get('search')?.toLowerCase()
      const sort = query.get('sort') ?? 'recent'

      let plants = [...db.plants]
      if (health && health !== 'all') plants = plants.filter((plant) => plant.health === health)
      if (governorate) plants = plants.filter((plant) => plant.location.governorate === governorate)
      if (search) {
        plants = plants.filter(
          (plant) =>
            plant.nickname.toLowerCase().includes(search) ||
            plant.species.commonName.toLowerCase().includes(search) ||
            plant.species.commonNameAr.includes(search),
        )
      }

      plants.sort((a, b) => {
        if (sort === 'name') return a.nickname.localeCompare(b.nickname)
        if (sort === 'health') return healthRank(b.health) - healthRank(a.health)
        if (sort === 'age') return b.ageMonths - a.ageMonths
        return new Date(b.lastCheckInAt).getTime() - new Date(a.lastCheckInAt).getTime()
      })

      return plants
    },
    emptyState: [] satisfies Plant[],
  },
  {
    method: 'GET',
    pattern: '/plants/:id',
    handler: ({ params }) => requirePlant(params.id),
  },
  {
    method: 'POST',
    pattern: '/plants',
    handler: ({ body }) => {
      const speciesId = readString(body, 'speciesId')
      const nickname = readString(body, 'nickname')
      const governorate = readString(body, 'governorate')
      const district = readString(body, 'district') ?? ''

      const fieldErrors: Record<string, string> = {}
      if (!speciesId) fieldErrors.speciesId = 'Choose a species.'
      if (!nickname) fieldErrors.nickname = 'Give the plant a name you will recognise later.'
      if (!governorate) fieldErrors.governorate = 'Select the governorate where it is planted.'
      if (Object.keys(fieldErrors).length > 0) {
        throw new ApiError({
          status: 422,
          code: 'validation_failed',
          message: 'Some details are missing.',
          fieldErrors,
        })
      }

      const species = db.species.find((item) => item.id === speciesId)
      if (!species) {
        throw new ApiError({
          status: 404,
          code: 'species_not_found',
          message: 'That species is not in the catalogue yet.',
        })
      }

      const plant: Plant = {
        id: nextId('pl', 100),
        nickname: nickname as string,
        species,
        plantedOn: readString(body, 'plantedOn') ?? new Date().toISOString(),
        location: { governorate: governorate as Plant['location']['governorate'], district },
        health: 'thriving',
        ageMonths: 0,
        lastCheckInAt: new Date().toISOString(),
        nextCareDueAt: addDays(7),
        pointsEarned: 150,
        photoLabel: readString(body, 'photoLabel') ?? 'awaiting-first-verification.jpg',
      }

      db.plants.unshift(plant)
      db.user.plantsCount = db.plants.length
      awardPoints(150, `Planting registered — ${nickname}`, 'planting')
      db.activity.unshift({
        id: nextId('ac', 700),
        kind: 'planted',
        title: 'Plant registered',
        detail: `${nickname} · ${plant.location.governorate}`,
        occurredAt: plant.plantedOn,
        points: 150,
      })
      return plant
    },
  },
  {
    method: 'PATCH',
    pattern: '/plants/:id',
    handler: ({ params, body }) => {
      const plant = requirePlant(params.id)
      const nickname = readString(body, 'nickname')
      const notes = readString(body, 'notes')
      const health = readString(body, 'health') as Plant['health'] | undefined
      if (nickname) plant.nickname = nickname
      if (notes !== undefined) plant.notes = notes
      if (health) plant.health = health
      return plant
    },
  },
  {
    method: 'POST',
    pattern: '/plants/:id/verification',
    handler: ({ params, body }) => {
      const plant = requirePlant(params.id)
      const points = 30
      plant.lastCheckInAt = new Date().toISOString()
      plant.pointsEarned += points
      awardPoints(points, `Verification photo approved — ${plant.nickname}`, 'care')
      db.activity.unshift({
        id: nextId('ac', 700),
        kind: 'verified',
        title: 'Verification photo submitted',
        detail: `${plant.nickname} · awaiting coordinator approval`,
        occurredAt: new Date().toISOString(),
        points,
      })
      return {
        id: nextId('vs', 600),
        plantId: plant.id,
        submittedAt: new Date().toISOString(),
        photoLabel: readString(body, 'photoLabel') ?? 'verification-photo.jpg',
        status: 'pending' as const,
        pointsAwarded: points,
        reviewerNote: 'A coordinator normally reviews verification photos within 24 hours.',
      }
    },
  },
]
